
        import { pagePath, applyAccount, currentMarket, displayLocale } from '/src/services/market.js';
        import { isMetric, distance, speed as displaySpeed } from '/src/services/units.js';

        // Import from the bundled app assets
        console.log('Profile page script starting...');
        
        // We'll use the global services that are already loaded
        // First, let's load the necessary modules by importing from the built assets
        const API_URL = 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
        
        // Simple auth check using localStorage (same as AuthService)
        function isSignedIn() {
            const tokens = localStorage.getItem('zephyr_auth_tokens');
            if (!tokens) return false;
            try {
                const parsed = JSON.parse(tokens);
                return !!parsed.id_token;
            } catch {
                return false;
            }
        }

        function getUser() {
            const tokens = localStorage.getItem('zephyr_auth_tokens');
            if (!tokens) return null;
            try {
                const parsed = JSON.parse(tokens);
                const payload = JSON.parse(atob(parsed.id_token.split('.')[1]));
                return {
                    sub: payload.sub,
                    email: payload.email
                };
            } catch {
                return null;
            }
        }

        // Access token (not id_token) -- what subscription-status/
        // subscription-cancel verify via verifyAuth(), same as every other
        // authenticated endpoint in the app.
        function getAccessToken() {
            const tokens = localStorage.getItem('zephyr_auth_tokens');
            if (!tokens) return null;
            try {
                return JSON.parse(tokens).access_token || null;
            } catch {
                return null;
            }
        }

        // Profile service functions
        async function getProfile(userId) {
            const response = await fetch(`${API_URL}/api/profile?userId=${userId}`, {
                headers: { 'Authorization': `Bearer ${getAccessToken()}` }
            });
            if (response.status === 404) return null;
            if (!response.ok) throw new Error(`Failed to fetch profile: ${response.status}`);
            return await response.json();
        }

        async function updateProfile(userId, updates) {
            const response = await fetch(`${API_URL}/api/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getAccessToken()}`
                },
                body: JSON.stringify({ userId, ...updates })
            });
            if (!response.ok) throw new Error(`Failed to update profile: ${response.status}`);
            return await response.json();
        }

        // Fetches subscription status and shows the right card: the
        // cancel-able card (active, not yet scheduled), the "ending on X"
        // card (already scheduled), or neither (not a subscriber).
        async function checkSubscriptionStatus() {
            const token = getAccessToken();
            if (!token) return;

            try {
                const response = await fetch(`${API_URL}/api/subscription-status`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) return;
                const data = await response.json();

                const cancelCard = document.getElementById('cancelSubCard');
                const scheduledCard = document.getElementById('cancelSubScheduledCard');
                if (!data.isActive) return; // free user -- leave both hidden
                if (data.lifetime) return; // nothing in Stripe to cancel

                // Bought in the Android app: only Google Play can cancel it.
                if (data.source === 'play' && data.manageUrl && !data.cancelAtPeriodEnd) {
                    document.getElementById('cancelSubText').textContent =
                        'Your subscription is billed through Google Play. Cancel or change it in Google Play; you keep Premium access until the end of the period you paid for.';
                    const btn = document.getElementById('cancelSubBtn');
                    btn.textContent = 'Manage in Google Play';
                    btn.onclick = () => { window.open(data.manageUrl, '_blank'); };
                }

                if (data.cancelAtPeriodEnd) {
                    const endDate = data.currentPeriodEnd
                        ? new Date(data.currentPeriodEnd * 1000).toLocaleDateString(displayLocale(), { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'the end of your current billing period';
                    document.getElementById('cancelSubScheduledText').textContent =
                        `Your subscription is set to end on ${endDate}. You'll keep Premium access until then.`;
                    scheduledCard.style.display = 'block';
                } else {
                    cancelCard.style.display = 'block';
                }
            } catch (err) {
                console.error('Subscription status check failed:', err);
            }
        }

        window.cancelSubscription = async function() {
            const confirmed = confirm("Cancel your subscription? You'll keep Premium access through the end of your current billing period, then it will lapse. This does not issue a refund.");
            if (!confirmed) return;

            const token = getAccessToken();
            if (!token) { alert('Not signed in.'); return; }

            try {
                const response = await fetch(`${API_URL}/api/subscription-cancel`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const data = await response.json();

                if (data.status === 'success') {
                    document.getElementById('cancelSubCard').style.display = 'none';
                    const endDate = data.currentPeriodEnd
                        ? new Date(data.currentPeriodEnd * 1000).toLocaleDateString(displayLocale(), { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'the end of your current billing period';
                    document.getElementById('cancelSubScheduledText').textContent =
                        `Your subscription is set to end on ${endDate}. You'll keep Premium access until then.`;
                    document.getElementById('cancelSubScheduledCard').style.display = 'block';
                } else {
                    alert('Failed to cancel subscription: ' + (data.message || 'Unknown error'));
                }
            } catch (err) {
                console.error('Cancel subscription error:', err);
                alert('Failed to cancel subscription. Please try again.');
            }
        };

        let currentProfile = null;

        async function loadProfile() {
            console.log('loadProfile called');
            try {
                // Check if user is signed in
                console.log('Checking if signed in...');
                if (!isSignedIn()) {
                    console.log('Not signed in, redirecting...');
                    window.location.href = pagePath('/app.html?signin=true');
                    return;
                }

                console.log('User is signed in');
                const user = getUser();
                console.log('User:', user);
                
                // Add timeout protection (10 seconds)
                const timeoutId = setTimeout(() => {
                    showError('Request is taking longer than expected. Please refresh the page.');
                    document.getElementById('loading').style.display = 'none';
                }, 10000);
                
                console.log('Fetching profile...');
                const profile = await getProfile(user.sub);
                console.log('Profile fetched:', profile);
                
                // Clear timeout on success
                clearTimeout(timeoutId);

                if (!profile) {
                    console.log('No profile found');
                    showError('Profile not found. Please subscribe first.');
                    document.getElementById('loading').style.display = 'none';
                    return;
                }

                currentProfile = profile;
                displayProfile(profile);
                checkSubscriptionStatus(); // non-blocking -- shows/hides the cancel card once it resolves

                document.getElementById('loading').style.display = 'none';
                document.getElementById('content').style.display = 'block';
                console.log('Profile displayed successfully');

            } catch (error) {
                console.error('Error loading profile:', error);
                console.error('Error stack:', error.stack);
                
                // User-friendly error message
                if (error.name === 'TypeError' && error.message.includes('fetch')) {
                    showError('Network error. Please check your internet connection and try again.');
                } else {
                    showError('Failed to load profile. Please refresh the page.');
                }
                
                document.getElementById('loading').style.display = 'none';
            }
        }

        function displayProfile(profile) {
            applyAccount(profile);
            // Header
            document.getElementById('profileName').textContent = profile.displayName || 'Your Profile';
            document.getElementById('profileEmail').textContent = profile.email;

            // Founding member badge
            if (profile.isFoundingMember) {
                document.getElementById('foundingBadge').style.display = 'inline-flex';
                document.getElementById('foundingText').textContent = `Founding Member`;
            }

            // Profile settings
            document.getElementById('displayName').value = profile.displayName || '';

            // RV Specs
            if (profile.rvSpecs) {
                document.getElementById('rvType').value = profile.rvSpecs.type || '';
                document.getElementById('rvHeight').value = profile.rvSpecs.height || '';
                document.getElementById('rvWeight').value = profile.rvSpecs.weight || '';
                document.getElementById('rvLength').value = profile.rvSpecs.length || '';
                
                // Show tow vehicle section if trailer type
                const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(profile.rvSpecs.type);
                if (isTrailer && profile.rvSpecs.towVehicle) {
                    document.getElementById('towVehicleSection').style.display = 'block';
                    document.getElementById('towVehicleType').value = profile.rvSpecs.towVehicle.type || '';
                    document.getElementById('towVehicleEngine').value = profile.rvSpecs.towVehicle.engine || '';
                    document.getElementById('towCapacity').value = profile.rvSpecs.towVehicle.capacity || '';
                    document.getElementById('towVehicleWeight').value = profile.rvSpecs.towVehicle.weight || '';
                    document.getElementById('hasSwayControl').checked = profile.rvSpecs.towVehicle.hasSwayControl || false;
                    document.getElementById('hasTrailerBrakes').checked = profile.rvSpecs.towVehicle.hasTrailerBrakes || false;
                }
            }

            // Travel Preferences
            const tp = profile.travelPreferences;
            if (tp) {
                if (tp.avgCruisingSpeedMph) document.getElementById('avgCruisingSpeed').value = tp.avgCruisingSpeedMph;
                if (tp.maxSpeedMph) document.getElementById('maxSpeed').value = tp.maxSpeedMph;
                if (tp.preferredDriveTimeHours) document.getElementById('preferredDriveTime').value = tp.preferredDriveTimeHours;
                if (tp.maxDriveTimeHours) document.getElementById('maxDriveTime').value = tp.maxDriveTimeHours;
                if (tp.fuelStopDurationMinutes) {
                    const sel = document.getElementById('fuelStopDuration');
                    const v = String(tp.fuelStopDurationMinutes);
                    if (![...sel.options].some(o => o.value === v)) sel.add(new Option(`${v} min`, v));
                    sel.value = v;
                }
                if (tp.preferredDepartureTime) document.getElementById('preferredDepartureTime').value = tp.preferredDepartureTime;
                if (typeof tp.autoSuggestStops === 'boolean') document.getElementById('autoSuggestStops').checked = tp.autoSuggestStops;
            }

            if (profile.isAdmin === true) {
                document.getElementById('unitsSelect').value = isMetric() ? 'metric' : 'imperial';
                document.getElementById('marketSelect').value = currentMarket();
                document.getElementById('unitsCard').style.display = 'block';
            }

            // Privacy settings
            document.getElementById('publicProfile').checked = profile.sharePreferences?.publicProfile || false;
            document.getElementById('shareBadges').checked = profile.sharePreferences?.shareBadges || false;
            document.getElementById('shareStats').checked = profile.sharePreferences?.shareStats || false;
            document.getElementById('showOnLeaderboard').checked = profile.sharePreferences?.showOnLeaderboard || false;

            // Stats
            document.getElementById('totalRoutes').textContent = profile.stats?.totalRoutes || profile.stats?.tripsPlanned || 0;
            const milesPlanned = profile.stats?.totalMiles || profile.stats?.milesPlanned || 0;
            const metric = isMetric();
            document.getElementById('totalMiles').textContent = (metric ? Math.round(milesPlanned * 1.609344) : milesPlanned).toLocaleString();
            if (metric) document.getElementById('totalMiles').nextElementSibling.textContent = 'Kilometres Planned';
            document.getElementById('statesVisited').textContent = Object.keys(profile.visitedStates || {}).length || profile.stats?.statesVisited || 0;
        }

        // Save profile
        document.getElementById('profileForm').addEventListener('submit', async (e) => {
            e.preventDefault();
            const saveBtn = document.getElementById('saveBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';

            try {
                const user = getUser();
                const displayName = document.getElementById('displayName').value.trim();

                // Validate display name length
                if (displayName.length < 2) {
                    showError('Display name must be at least 2 characters');
                    return;
                }
                if (displayName.length > 50) {
                    showError('Display name must be 50 characters or less');
                    return;
                }

                await updateProfile(user.sub, { displayName });
                
                // Clear the profile cache so app.html will fetch fresh data
                const cacheKey = `zephyr_profile_${user.sub}`;
                localStorage.removeItem(cacheKey);
                
                showSuccess('Profile updated successfully!');
                
                // Update header
                document.getElementById('profileName').textContent = displayName;
                currentProfile.displayName = displayName;

            } catch (error) {
                console.error('Error saving profile:', error);
                showError('Failed to save profile. Please try again.');
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save Changes';
            }
        });

        document.getElementById('saveMarketBtn').addEventListener('click', async () => {
            const saveBtn = document.getElementById('saveMarketBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';
            try {
                const market = document.getElementById('marketSelect').value;
                await updateProfile(getUser().sub, { market });
                localStorage.removeItem('zephyr_user_profile');
                // Reload so every part of the page picks up the new market.
                window.location.reload();
            } catch (error) {
                console.error('Error saving market:', error);
                showError('Failed to save market. Please try again.');
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save Market';
            }
        });

        document.getElementById('saveUnitsBtn').addEventListener('click', async () => {
            const saveBtn = document.getElementById('saveUnitsBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';
            try {
                const units = document.getElementById('unitsSelect').value;
                await updateProfile(getUser().sub, { units });
                currentProfile.units = units;
                // The app caches the profile for 5 minutes; drop it so the change shows on next load.
                localStorage.removeItem('zephyr_user_profile');
                showSuccess(`Units set to ${units}. Reload the app to see it.`);
            } catch (error) {
                console.error('Error saving units:', error);
                showError('Failed to save units. Please try again.');
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save Units';
            }
        });

        // Save privacy settings
        document.getElementById('savePrivacyBtn').addEventListener('click', async () => {
            const saveBtn = document.getElementById('savePrivacyBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';

            try {
                const user = getUser();
                const sharePreferences = {
                    publicProfile: document.getElementById('publicProfile').checked,
                    shareBadges: document.getElementById('shareBadges').checked,
                    shareStats: document.getElementById('shareStats').checked,
                    showOnLeaderboard: document.getElementById('showOnLeaderboard').checked
                };

                await updateProfile(user.sub, { sharePreferences });
                showSuccess('Privacy settings updated successfully!');
                currentProfile.sharePreferences = sharePreferences;

            } catch (error) {
                console.error('Error saving privacy settings:', error);
                showError('Failed to save privacy settings. Please try again.');
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save Privacy Settings';
            }
        });

        // RV Type change handler - show/hide tow vehicle section
        document.getElementById('rvType').addEventListener('change', (e) => {
            const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(e.target.value);
            document.getElementById('towVehicleSection').style.display = isTrailer ? 'block' : 'none';
            // Hide sway control for fifth-wheels (only travel trailers need it)
            const swayGroup = document.getElementById('profileSwayControlGroup');
            if (swayGroup) {
                swayGroup.style.display = e.target.value === 'fifth-wheel' ? 'none' : '';
            }
        });

        // Save RV specs
        document.getElementById('saveRvSpecsBtn').addEventListener('click', async () => {
            const saveBtn = document.getElementById('saveRvSpecsBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';

            try {
                const user = getUser();
                const rvType = document.getElementById('rvType').value;
                
                // Build rvSpecs object
                const rvSpecs = {
                    type: rvType,
                    height: parseFloat(document.getElementById('rvHeight').value) || null,
                    weight: parseFloat(document.getElementById('rvWeight').value) || null,
                    length: parseFloat(document.getElementById('rvLength').value) || null
                };
                
                // Add tow vehicle if trailer type
                const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(rvType);
                if (isTrailer) {
                    const towVehicleType = document.getElementById('towVehicleType').value;
                    if (towVehicleType) {
                        rvSpecs.towVehicle = {
                            type: towVehicleType,
                            engine: document.getElementById('towVehicleEngine').value || null,
                            capacity: parseFloat(document.getElementById('towCapacity').value) || null,
                            weight: parseFloat(document.getElementById('towVehicleWeight').value) || null,
                            hasSwayControl: document.getElementById('hasSwayControl').checked,
                            hasTrailerBrakes: document.getElementById('hasTrailerBrakes').checked
                        };
                    }
                }

                await updateProfile(user.sub, { rvSpecs });
                showSuccess('RV specifications saved successfully!');
                
                // Clear cache so app.html will fetch fresh data
                const cacheKey = `zephyr_profile_${user.sub}`;
                localStorage.removeItem(cacheKey);
                
                currentProfile.rvSpecs = rvSpecs;

            } catch (error) {
                console.error('Error saving RV specs:', error);
                showError('Failed to save RV specs. Please try again.');
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save RV Specifications';
            }
        });

        // Save travel preferences
        document.getElementById('saveTravelPrefsBtn').addEventListener('click', async () => {
            const saveBtn = document.getElementById('saveTravelPrefsBtn');
            saveBtn.disabled = true;
            saveBtn.textContent = 'Saving...';

            try {
                const user = getUser();
                const travelPreferences = {
                    avgCruisingSpeedMph: parseInt(document.getElementById('avgCruisingSpeed').value) || 60,
                    maxSpeedMph: parseInt(document.getElementById('maxSpeed').value) || 65,
                    preferredDriveTimeHours: parseFloat(document.getElementById('preferredDriveTime').value) || 6,
                    maxDriveTimeHours: parseFloat(document.getElementById('maxDriveTime').value) || 8,
                    fuelStopDurationMinutes: parseInt(document.getElementById('fuelStopDuration').value) || 30,
                    preferredDepartureTime: document.getElementById('preferredDepartureTime').value || '08:00',
                    autoSuggestStops: document.getElementById('autoSuggestStops').checked
                };

                // Save to rig-profile API (anonymous storage)
                await fetch(`${API_URL}/api/rig-profile`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getAccessToken()}`
                    },
                    body: JSON.stringify({ userId: user.sub, travelPreferences })
                });

                // Also save to user profile for backward compatibility
                await updateProfile(user.sub, { travelPreferences });
                showSuccess('Travel style saved successfully!');
                
                // Clear cache
                const cacheKey = `zephyr_profile_${user.sub}`;
                localStorage.removeItem(cacheKey);

            } catch (error) {
                console.error('Error saving travel style:', error);
                showError('Failed to save travel style. Please try again.');
            } finally {
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save Travel Style';
            }
        });

        // Drive time preview
        function updateDriveTimePreview() {
            const speed = parseInt(document.getElementById('avgCruisingSpeed').value) || 60;
            const prefHours = parseFloat(document.getElementById('preferredDriveTime').value) || 6;
            const preview = document.getElementById('driveTimePreview');
            const miles = Math.round(speed * prefHours);
            const stops = miles > 300 ? Math.floor(miles / (speed * prefHours)) : 0;
            preview.style.display = 'block';
            const speedMph = parseFloat(document.getElementById('avgCruisingSpeed').value) || 60;
            const route = isMetric()
                ? `${distance(speedMph * prefHours)} km route at ${displaySpeed(speedMph)} km/h`
                : `${miles} mile route at ${speed}mph`;
            preview.textContent = `On a ${route} you'd drive ~${prefHours} hours. Zephyr would suggest ${stops > 0 ? stops + ' stop(s)' : 'no stops'} before your destination.`;
        }

        document.getElementById('avgCruisingSpeed').addEventListener('input', updateDriveTimePreview);
        document.getElementById('preferredDriveTime').addEventListener('input', updateDriveTimePreview);
        updateDriveTimePreview();

        function showError(message) {
            const errorDiv = document.getElementById('error');
            errorDiv.textContent = message;
            errorDiv.style.display = 'block';
            setTimeout(() => errorDiv.style.display = 'none', 5000);
        }

        function showSuccess(message) {
            const successDiv = document.getElementById('success');
            successDiv.textContent = message;
            successDiv.style.display = 'block';
            setTimeout(() => successDiv.style.display = 'none', 3000);
        }

        // Load profile on page load
        loadProfile();
    